import React, { createContext, useContext, useState } from 'react';
import { authApi, api } from '../api/client';
import { neighbors as mockNeighbors } from '../data/mockData';

const AppContext = createContext(null);

/**
 * AppContext now authenticates against the FastAPI backend.
 * Demo credentials remain identical:
 *   - Staff: exec/pm/cm/cc@jbnc.org, partner@eastside.org  (password: demo)
 *   - Admin: admin@district.app (password: demo)
 *   - Neighbor: any of 12 phone numbers (317-555-1001 through 317-555-1010,
 *     1013, 1023)  OTP: 123456
 */
export const AppProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('district_user')); } catch { return null; }
  });
  const [selectedTenant, setSelectedTenant] = useState('tenant_jbnc');

  const persist = (user, token) => {
    setCurrentUser(user);
    if (user) {
      localStorage.setItem('district_user', JSON.stringify(user));
      if (token) localStorage.setItem('district_token', token);
      // Let DataStore know to re-fetch now that we have a token
      try { window.dispatchEvent(new CustomEvent('district:auth-change')); } catch { /* ignore */ }
    } else {
      localStorage.removeItem('district_user');
      localStorage.removeItem('district_token');
      try { window.dispatchEvent(new CustomEvent('district:auth-change')); } catch { /* ignore */ }
    }
  };

  const adminLogin = async (email, password) => {
    try {
      const r = await authApi.adminLogin(email, password);
      if (r.success) { persist(r.user, r.token); return { success: true }; }
      return { success: false, error: r.error || 'Invalid credentials' };
    } catch (e) {
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const staffLogin = async (email, password) => {
    try {
      const r = await authApi.staffLogin(email, password);
      if (r.success) { persist(r.user, r.token); return { success: true, role: r.user.role }; }
      return { success: false, error: r.error || 'Invalid credentials' };
    } catch (e) {
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const neighborLogin = async (phone, otp) => {
    try {
      const r = await authApi.neighborLogin(phone, otp);
      if (r.success) { persist(r.user, r.token); return { success: true, isNew: false }; }
      return { success: false, error: r.error || 'Invalid OTP' };
    } catch (e) {
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const registerNeighbor = async (formData) => {
    // Self-registration now persists to the backend neighbor catalog so the
    // record is real (and the eligibility prefill mapper picks up the rich
    // form data on day-one of intake). Falls back to the mock-only flow
    // when the API is unreachable so the demo never blocks.
    try {
      const res = await api.post('/neighbors', { ...formData, source: 'self_registration' });
      const created = res?.neighbor;
      if (created?.id) {
        const user = {
          id: created.id,
          neighborId: created.id,
          name: `${created.firstName || formData.firstName} ${created.lastName || formData.lastName}`.trim(),
          phone: created.phone || formData.phone,
          role: 'Neighbor',
          portal: 'portal',
          isNew: true,
          approvalStatus: created.approvalStatus || 'pending',
        };
        persist(user);
        return { success: true, neighborId: created.id, duplicate: res.duplicate };
      }
    } catch (e) {
      console.warn('[registerNeighbor] backend create failed — falling back to in-session', e);
    }
    // Fallback: in-session demo neighbor bootstrapped against nbr_1.
    const fallback = mockNeighbors[0];
    const user = {
      id: fallback.id,
      neighborId: fallback.id,
      name: `${formData.firstName} ${formData.lastName}`,
      phone: formData.phone,
      role: 'Neighbor',
      portal: 'portal',
      isNew: true,
    };
    persist(user);
    return { success: true, fallback: true };
  };

  const logout = () => persist(null, null);

  const isAuthenticated = !!currentUser;
  const userRole = currentUser?.role;
  const userPortal = currentUser?.portal;

  return (
    <AppContext.Provider value={{ currentUser, isAuthenticated, userRole, userPortal, adminLogin, staffLogin, neighborLogin, registerNeighbor, logout, selectedTenant, setSelectedTenant }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
